import { useState } from "react";
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";
import { C, Btn, Input, Label } from "../components/shared";

export default function BodyshopLogin({ onBack, onPending, onSuccess }) {
  const [mode, setMode] = useState("login"); // login | signup
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [login, setLogin] = useState({ email:"", password:"" });
  const [reg, setReg] = useState({ businessName:"", ownerName:"", postcode:"", companiesHouse:"", phone:"", email:"", password:"", radius:"25" });

  const doLogin = async () => {
    if (!login.email || !login.password) { setError("Please enter your email and password."); return; }
    setLoading(true); setError("");
    try {
      const cred = await signInWithEmailAndPassword(auth, login.email, login.password);
      const profileSnap = await getDoc(doc(db, "users", cred.user.uid));
      if (profileSnap.exists() && profileSnap.data().status === "pending") {
        onPending();
      } else {
        onSuccess();
      }
    } catch (e) {
      setError("Incorrect email or password. Please try again.");
    }
    setLoading(false);
  };

  const doRegister = async () => {
    if (!reg.businessName || !reg.ownerName || !reg.postcode || !reg.companiesHouse || !reg.phone || !reg.email || !reg.password) {
      setError("Please fill in all fields."); return;
    }
    setLoading(true); setError("");
    try {
      const cred = await createUserWithEmailAndPassword(auth, reg.email, reg.password);
      await setDoc(doc(db, "users", cred.user.uid), {
        role: "bodyshop",
        status: "pending",
        businessName: reg.businessName,
        ownerName: reg.ownerName,
        postcode: reg.postcode.toUpperCase(),
        companiesHouse: reg.companiesHouse,
        phone: reg.phone,
        email: reg.email,
        radius: parseInt(reg.radius),
        joinedAt: serverTimestamp(),
      });
      onPending();
    } catch (e) {
      if (e.code === "auth/email-already-in-use") setError("An account with this email already exists. Please sign in.");
      else if (e.code === "auth/weak-password") setError("Password must be at least 6 characters.");
      else setError("Registration failed. Please try again.");
    }
    setLoading(false);
  };

  const base = { minHeight:"100vh", background:C.dark, display:"flex", flexDirection:"column", justifyContent:"center", padding:"24px 20px", color:C.text, fontFamily:"'DM Sans',sans-serif" };
  const card = { background:C.card, border:`1px solid ${C.border}`, borderRadius:20, padding:"24px 20px", maxWidth:460, margin:"0 auto", width:"100%" };

  if (mode === "signup") return (
    <div style={base}>
      <div style={{ maxWidth:460, margin:"0 auto", width:"100%" }}>
        <button onClick={()=>setMode("login")} style={{ background:"transparent", border:"none", color:C.muted, cursor:"pointer", fontFamily:"inherit", fontSize:13, display:"flex", alignItems:"center", gap:6, padding:"0 0 20px" }}>← Back to sign in</button>
      </div>
      <div style={card}>
        <div style={{ display:"inline-flex", alignItems:"center", gap:6, background:"rgba(22,163,74,0.1)", border:"1px solid rgba(22,163,74,0.25)", color:C.greenLight, fontSize:12, padding:"5px 13px", borderRadius:20, marginBottom:16, fontWeight:700 }}>✓ Free to join — pay only per lead</div>
        <h2 style={{ fontSize:22, fontWeight:900, marginBottom:6 }}>Register your bodyshop</h2>
        <p style={{ fontSize:13, color:"#999", marginBottom:22, lineHeight:1.6 }}>Join the UK's fastest growing bodywork lead platform. No monthly fees.</p>
        {[["businessName","Business name","e.g. Rochdale Bodyworks Ltd","text"],["ownerName","Your full name","Owner / manager name","text"],["postcode","Business postcode","e.g. OL11 1AA","text"],["companiesHouse","Companies House number","8-digit number · required for verification","text"],["phone","Business phone","07700 900000","tel"],["email","Email address","you@yourbodyshop.co.uk","email"],["password","Password (min. 6 characters)","Create a secure password","password"]].map(([k,lbl,ph,tp])=>(
          <div key={k} style={{ marginBottom:14 }}>
            <Label>{lbl}</Label>
            <input type={tp} value={reg[k]} onChange={e=>setReg(r=>({...r,[k]:e.target.value}))} placeholder={ph}
              style={{ width:"100%", background:"#0D0D0D", border:`1.5px solid ${C.border}`, borderRadius:12, padding:"13px 14px", color:C.text, fontFamily:"inherit", fontSize:14, outline:"none", boxSizing:"border-box" }} />
          </div>
        ))}
        {error && <div style={{ background:"#2d1010", border:"1px solid #6b2020", borderRadius:10, padding:"11px 14px", fontSize:12, color:"#f87171", marginBottom:14, lineHeight:1.6 }}>{error}</div>}
        <button onClick={doRegister} disabled={loading}
          style={{ width:"100%", background: loading?"#666":C.red, color:"#fff", border:"none", borderRadius:13, padding:"15px", fontSize:15, fontWeight:800, cursor: loading?"not-allowed":"pointer", fontFamily:"inherit", marginTop:8 }}>
          {loading ? "Creating account..." : "Create account →"}
        </button>
      </div>
    </div>
  );

  return (
    <div style={base}>
      <button onClick={onBack} style={{ background:"transparent", border:"none", color:C.muted, cursor:"pointer", fontFamily:"inherit", fontSize:13, display:"flex", alignItems:"center", gap:6, padding:"0 0 24px", maxWidth:460, margin:"0 auto", width:"100%" }}>← Back</button>
      <div style={{ fontSize:20, fontWeight:900, color:C.red, textAlign:"center", marginBottom:3 }}>QuoteMyBodywork</div>
      <div style={{ textAlign:"center", fontSize:13, color:C.muted, marginBottom:24 }}>Bodyshop partner portal</div>
      <div style={card}>
        <h2 style={{ fontSize:20, fontWeight:900, marginBottom:6 }}>Sign in</h2>
        <p style={{ fontSize:13, color:"#999", marginBottom:22, lineHeight:1.6 }}>Access your lead feed and purchased contact details.</p>
        <div style={{ marginBottom:14 }}><Label>Email address</Label><Input value={login.email} onChange={v=>setLogin(l=>({...l,email:v}))} placeholder="you@yourbodyshop.co.uk" type="email" /></div>
        <div style={{ marginBottom:14 }}><Label>Password</Label><Input value={login.password} onChange={v=>setLogin(l=>({...l,password:v}))} placeholder="Your password" type="password" /></div>
        {error && <div style={{ background:"#2d1010", border:"1px solid #6b2020", borderRadius:10, padding:"11px 14px", fontSize:12, color:"#f87171", marginBottom:14 }}>{error}</div>}
        <button onClick={doLogin} disabled={loading}
          style={{ width:"100%", background: loading?"#666":C.red, color:"#fff", border:"none", borderRadius:13, padding:"15px", fontSize:15, fontWeight:800, cursor: loading?"not-allowed":"pointer", fontFamily:"inherit" }}>
          {loading ? "Signing in..." : "Sign in →"}
        </button>
        <div onClick={()=>setMode("signup")} style={{ textAlign:"center", fontSize:13, color:C.muted, marginTop:18, cursor:"pointer" }}>
          New bodyshop? <span style={{ color:C.red, fontWeight:700 }}>Register free →</span>
        </div>
      </div>
    </div>
  );
}
